import React from 'react';
import { useSettings } from '@/context/SettingsContext';

const ReceiptPrint = React.forwardRef(({ receipt, receiptType = "payment" }, ref) => {
  const { formatCurrency, formatDate, settings } = useSettings();
  
  const getReceiptTitle = () => {
    if (receiptType === "admission") return "Admission Fee Receipt";
    if (receiptType === "subscription") return "Subscription Fee Receipt";
    return "Payment Receipt";
  };

  return (
    <div ref={ref} className="receipt-print-container" style={{ display: 'none' }}>
      {/* A4/A5 Format */}
      <div className="receipt-a4" style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '20mm',
        margin: '0 auto',
        backgroundColor: 'white',
        fontFamily: 'Arial, sans-serif'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '3px solid #000', paddingBottom: '20px' }}>
          {receipt?.company_logo_url && (
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}${receipt.company_logo_url}`}
              alt="Company Logo" 
              style={{ maxHeight: '80px', maxWidth: '200px', margin: '0 auto 15px', display: 'block' }}
            />
          )}
          <h1 style={{ margin: '0', fontSize: '32px', fontWeight: 'bold' }}>{receipt?.company_name || 'IronPulse Gym'}</h1>
          <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>{receipt?.company_address || ''}</p>
          <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
            Phone: {receipt?.company_phone || ''} | Email: {receipt?.company_email || ''}
          </p>
        </div>

        {/* Receipt Title */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h2 style={{ margin: '0', fontSize: '24px', textTransform: 'uppercase', letterSpacing: '2px' }}>
            {getReceiptTitle()}
          </h2>
        </div>

        {/* Receipt Details */}
        <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Receipt No:</strong> {receipt?.receipt_number}
            </p>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Date:</strong> {formatDate(receipt?.receipt_date || receipt?.date)}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Payment Method:</strong> {receipt?.payment_method?.replace('_', ' ').toUpperCase()}
            </p>
          </div>
        </div>

        {/* Member/Customer Details */}
        {receipt?.member_name && (
          <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Received From:</h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Name:</strong> {receipt.member_name}
            </p>
            {receipt.member_email && (
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Email:</strong> {receipt.member_email}
              </p>
            )}
            {receipt.member_phone && (
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Phone:</strong> {receipt.member_phone}
              </p>
            )}
          </div>
        )}

        {/* Payment Details */}
        <div style={{ marginBottom: '30px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #000', fontSize: '14px' }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #000', fontSize: '14px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #000', fontSize: '14px' }}>
                  {receipt?.description || getReceiptTitle()}
                  {receipt?.plan_name && ` - ${receipt.plan_name}`}
                  {receipt?.period && ` (${receipt.period})`}
                </td>
                <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #000', fontSize: '14px' }}>
                  {formatCurrency(receipt?.amount)}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <td style={{ padding: '12px', border: '1px solid #000', fontSize: '16px', fontWeight: 'bold' }}>
                  Total Amount
                </td>
                <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #000', fontSize: '18px', fontWeight: 'bold' }}>
                  {formatCurrency(receipt?.amount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Amount in Words */}
        <div style={{ marginBottom: '30px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
          <p style={{ margin: '0', fontSize: '14px' }}>
            <strong>Amount in Words:</strong> {receipt?.amount_in_words || ''}
          </p>
        </div>

        {/* Notes */}
        {receipt?.notes && (
          <div style={{ marginBottom: '30px' }}>
            <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
              <strong>Note:</strong> {receipt.notes}
            </p>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '50px', paddingTop: '20px', borderTop: '1px solid #ccc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>Signature: ___________________</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>Authorized Signatory</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <p style={{ margin: '5px 0', fontSize: '11px', color: '#888' }}>
              This is a computer-generated receipt and does not require a signature.
            </p>
            <p style={{ margin: '5px 0', fontSize: '11px', color: '#888' }}>
              Thank you for your payment!
            </p>
          </div>
        </div>
      </div>

      {/* Thermal Printer Format (58mm/80mm) */}
      <div className="receipt-thermal" style={{
        width: '80mm',
        padding: '5mm',
        margin: '0 auto',
        backgroundColor: 'white',
        fontFamily: 'Courier, monospace',
        fontSize: '12px'
      }}>
        <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '10px', marginBottom: '10px' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
            {receipt?.company_name || 'IronPulse Gym'}
          </div>
          <div style={{ fontSize: '10px' }}>{receipt?.company_address || ''}</div>
          <div style={{ fontSize: '10px' }}>Ph: {receipt?.company_phone || ''}</div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{getReceiptTitle()}</div>
        </div>

        <div style={{ marginBottom: '10px', fontSize: '11px' }}>
          <div>Receipt: {receipt?.receipt_number}</div>
          <div>Date: {formatDate(receipt?.receipt_date || receipt?.date)}</div>
          <div>Method: {receipt?.payment_method?.toUpperCase()}</div>
        </div>

        {receipt?.member_name && (
          <div style={{ borderTop: '1px dashed #000', borderBottom: '1px dashed #000', padding: '5px 0', marginBottom: '10px', fontSize: '11px' }}>
            <div><strong>Customer:</strong> {receipt.member_name}</div>
            {receipt.member_phone && <div>Ph: {receipt.member_phone}</div>}
          </div>
        )}

        <div style={{ marginBottom: '10px', fontSize: '11px' }}>
          <div>{receipt?.description || getReceiptTitle()}</div>
          <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 'bold', marginTop: '5px' }}>
            {formatCurrency(receipt?.amount)}
          </div>
        </div>

        <div style={{ borderTop: '1px dashed #000', paddingTop: '10px', marginTop: '10px', textAlign: 'center', fontSize: '10px' }}>
          <div>Thank you for your payment!</div>
          <div style={{ marginTop: '5px' }}>Powered by IronPulse Manager</div>
        </div>
      </div>
    </div>
  );
});

ReceiptPrint.displayName = 'ReceiptPrint';

export default ReceiptPrint;
